import { Component } from "react";
import { Link } from "react-router-dom";
import { mixHexColors } from "@/lib/util";
import { translate } from "@/modules/shared/IntlProviderWrapper";

import "./PeopleCompetence.scss";

type Props = {
    level: number;
    title: string;
    line_uuid?: string;
    score?: number;
    /** Max score among visible lines for this person; used to normalize score coloring. */
    score_max?: number;
    dwell?: number;
    warmup?: number;
};

const COLOR_NONE = "#e4e7eb"; // light gray
const COLOR_ZERO_BLUE = "#b3e5fc"; // light blue
const COLOR_ONE_ORANGE = "#ffd4b3"; // light orange
const COLOR_ONE_GREEN = "#c6f6d5"; // light green

function getRelativeBackground(value: number | undefined, zero_hex: string, one_hex: string): string {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return COLOR_NONE;
    }
    const t = Math.min(1, Math.max(0, value));
    return mixHexColors(zero_hex, one_hex, t);
}

function getWarmupBackground(warmup: number | undefined): string {
    return getRelativeBackground(warmup, COLOR_ZERO_BLUE, COLOR_ONE_ORANGE);
}

function getScoreBackground(score: number | undefined, score_max: number | undefined): string {
    if (score === undefined || score === null || Number.isNaN(score)) {
        return COLOR_NONE;
    }
    if (score_max === undefined || score_max === null || Number.isNaN(score_max) || score_max <= 0) {
        // Single/zero max: treat present score as full intensity when > 0, else zero.
        return getRelativeBackground(score > 0 ? 1 : 0, COLOR_ZERO_BLUE, COLOR_ONE_GREEN);
    }
    return getRelativeBackground(score / score_max, COLOR_ZERO_BLUE, COLOR_ONE_GREEN);
}

/**
 * Display single row that represents person's competence in certain area (e.g. line, process, tool etc.).
 */
class PeopleCompetence extends Component<Props> {
    renderScoreValue(value: number | undefined): string {
        if (value === undefined || value === null || Number.isNaN(value)) {
            return "—";
        }
        return String(value);
    }

    render() {
        // Extract competence label from level
        let competence_label = translate("Manufacturing.People.competence_level.0", "None");
        if (this.props.level === 1) {
            competence_label = translate("Manufacturing.People.competence_level.1", "Learning");
        } else if (this.props.level === 2) {
            competence_label = translate("Manufacturing.People.competence_level.2", "Capable");
        } else if (this.props.level === 3) {
            competence_label = translate("Manufacturing.People.competence_level.3", "Independent");
        }

        const content = (
            <>
                <span className="people-competence__title">{this.props.title}</span>
                <span className="people-competence__scores">
                    <span
                        className="people-competence__score people-competence__score--metric"
                        title={translate("PersonLineModal.score", "Score")}
                        style={{
                            backgroundColor: getScoreBackground(this.props.score, this.props.score_max),
                        }}
                    >
                        <span className="people-competence__score-label">
                            {translate("PersonLineModal.score", "Score")}
                        </span>
                        {this.renderScoreValue(this.props.score)}
                    </span>
                    <span
                        className="people-competence__score people-competence__score--metric"
                        title={translate("PersonLineModal.warmup", "Warmup")}
                        style={{ backgroundColor: getWarmupBackground(this.props.warmup) }}
                    >
                        <span className="people-competence__score-label">
                            {translate("PersonLineModal.warmup", "Warmup")}
                        </span>
                        {this.renderScoreValue(this.props.warmup)}
                    </span>
                </span>
                <span className={"badge badge-competence badge-competence-" + this.props.level}>
                    {competence_label}
                </span>
            </>
        );

        if (this.props.line_uuid) {
            return (
                <Link
                    className="list-group-item list-group-item-action d-flex align-items-center people-competence"
                    to={`/digital-twin/resources/lines/${this.props.line_uuid}`}
                    title={translate("Manufacturing.People.view_line", "View line")}
                >
                    {content}
                </Link>
            );
        }

        return <li className="list-group-item d-flex align-items-center people-competence">{content}</li>;
    }
}

export default PeopleCompetence;
